import { PlayAnswerDto } from '@routellers/types/dist/openapi/city-quest/play-answer.dto';
import { PlayArCollectDto } from '@routellers/types/dist/openapi/city-quest/play-ar-collect.dto';
import { PlayChaseResultDto } from '@routellers/types/dist/openapi/city-quest/play-chase-result.dto';
import { PlayFinaleChoiceDto } from '@routellers/types/dist/openapi/city-quest/play-finale-choice.dto';
import { PlayHintDto } from '@routellers/types/dist/openapi/city-quest/play-hint.dto';
import { PlayIssueDto } from '@routellers/types/dist/openapi/city-quest/play-issue.dto';
import { PlayPhotoDto } from '@routellers/types/dist/openapi/city-quest/play-photo.dto';
import { PlaySecretAnswerDto } from '@routellers/types/dist/openapi/city-quest/play-secret.dto';
import { PlaySelectStopDto } from '@routellers/types/dist/openapi/city-quest/play-select-stop.dto';
import { PlayStartSessionDto } from '@routellers/types/dist/openapi/city-quest/play-start-session.dto';
import {
    PlayAccessInfoRO,
    PlayAnswerResultRO,
    PlayPhotoResultRO,
    PlayStateRO,
} from '@routellers/types/dist/openapi/city-quest/play-state.ro';

import { ApiMethodDeclaration } from '..';

/**
 * Публичный API игрока городского квеста (без авторизации — токен
 * доступа/сессии в URL является «паролем»).
 */

const getAccessInfo: ApiMethodDeclaration<string, PlayAccessInfoRO> = (token) => ({
    url: `/openapi/city-quest/access/${token}`,
    method: 'GET',
});

const startSession: ApiMethodDeclaration<{ data: PlayStartSessionDto }, PlayStateRO> = ({
    data,
}) => ({
    url: '/openapi/city-quest/sessions',
    method: 'POST',
    data,
});

const getState: ApiMethodDeclaration<string, PlayStateRO> = (token) => ({
    url: `/openapi/city-quest/sessions/${token}`,
    method: 'GET',
});

const arrival: ApiMethodDeclaration<string, PlayStateRO> = (token) => ({
    url: `/openapi/city-quest/sessions/${token}/arrival`,
    method: 'POST',
});

const answer: ApiMethodDeclaration<{ token: string; data: PlayAnswerDto }, PlayAnswerResultRO> = ({
    token,
    data,
}) => ({
    url: `/openapi/city-quest/sessions/${token}/answer`,
    method: 'POST',
    data,
});

const hint: ApiMethodDeclaration<
    { token: string; data: PlayHintDto },
    { text: string; state: PlayStateRO }
> = ({ token, data }) => ({
    url: `/openapi/city-quest/sessions/${token}/hint`,
    method: 'POST',
    data,
});

const skip: ApiMethodDeclaration<string, PlayAnswerResultRO> = (token) => ({
    url: `/openapi/city-quest/sessions/${token}/skip`,
    method: 'POST',
});

const routeEventAnswer: ApiMethodDeclaration<
    { token: string; data: PlayAnswerDto },
    PlayAnswerResultRO
> = ({ token, data }) => ({
    url: `/openapi/city-quest/sessions/${token}/route-event/answer`,
    method: 'POST',
    data,
});

const routeEventDismiss: ApiMethodDeclaration<string, PlayStateRO> = (token) => ({
    url: `/openapi/city-quest/sessions/${token}/route-event/dismiss`,
    method: 'POST',
});

const finaleAssembly: ApiMethodDeclaration<
    { token: string; data: PlayAnswerDto },
    PlayAnswerResultRO
> = ({ token, data }) => ({
    url: `/openapi/city-quest/sessions/${token}/finale/assembly`,
    method: 'POST',
    data,
});

const finaleChoice: ApiMethodDeclaration<
    { token: string; data: PlayFinaleChoiceDto },
    PlayStateRO
> = ({ token, data }) => ({
    url: `/openapi/city-quest/sessions/${token}/finale/choice`,
    method: 'POST',
    data,
});

/** Свободный порядок прохождения: выбрать следующую точку. */
const selectStop: ApiMethodDeclaration<{ token: string; data: PlaySelectStopDto }, PlayStateRO> = ({
    token,
    data,
}) => ({
    url: `/openapi/city-quest/sessions/${token}/select-stop`,
    method: 'POST',
    data,
});

/** Бонус-секрет точки: «осмотреться» — открыть prompt секрета. */
const secretReveal: ApiMethodDeclaration<string, PlayStateRO> = (token) => ({
    url: `/openapi/city-quest/sessions/${token}/secret/reveal`,
    method: 'POST',
});

/** Бонус-секрет точки: ответ (без штрафа за ошибку). */
const secretAnswer: ApiMethodDeclaration<
    { token: string; data: PlaySecretAnswerDto },
    { correct: boolean; solutionExplanation?: string; state: PlayStateRO }
> = ({ token, data }) => ({
    url: `/openapi/city-quest/sessions/${token}/secret/answer`,
    method: 'POST',
    data,
});

/** Исход GPS-погони (клиентская мини-игра route-события gps_chase). */
const chaseResult: ApiMethodDeclaration<
    { token: string; data: PlayChaseResultDto },
    PlayStateRO
> = ({ token, data }) => ({
    url: `/openapi/city-quest/sessions/${token}/route-event/chase-result`,
    method: 'POST',
    data,
});

/** Сбор символа AR-охоты (challenge.type === 'ar_hunt'). Идемпотентен. */
const arCollect: ApiMethodDeclaration<{ token: string; data: PlayArCollectDto }, PlayStateRO> = ({
    token,
    data,
}) => ({
    url: `/openapi/city-quest/sessions/${token}/ar-collect`,
    method: 'POST',
    data,
});

/** Фото-задание с AI-верификацией: снимок как data URL. */
const submitPhoto: ApiMethodDeclaration<
    { token: string; data: PlayPhotoDto },
    PlayPhotoResultRO
> = ({ token, data }) => ({
    url: `/openapi/city-quest/sessions/${token}/photo`,
    method: 'POST',
    data,
});

const pause: ApiMethodDeclaration<string, PlayStateRO> = (token) => ({
    url: `/openapi/city-quest/sessions/${token}/pause`,
    method: 'PATCH',
});

const resume: ApiMethodDeclaration<string, PlayStateRO> = (token) => ({
    url: `/openapi/city-quest/sessions/${token}/resume`,
    method: 'PATCH',
});

const reportIssue: ApiMethodDeclaration<{ token: string; data: PlayIssueDto }, PlayStateRO> = ({
    token,
    data,
}) => ({
    url: `/openapi/city-quest/sessions/${token}/issue`,
    method: 'POST',
    data,
});

export const cityQuestPlayApiDeclaration = {
    getAccessInfo,
    startSession,
    getState,
    arrival,
    answer,
    hint,
    skip,
    routeEventAnswer,
    routeEventDismiss,
    selectStop,
    secretReveal,
    secretAnswer,
    chaseResult,
    arCollect,
    submitPhoto,
    finaleAssembly,
    finaleChoice,
    pause,
    resume,
    reportIssue,
};
